import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';

/**
 * Mobile-first bottom navigation matching the SO Screens design (Home / Watch /
 * Sales / Payouts / Wallet). Pure navigation chrome — every tab routes to a
 * page that already exists in PartnerRoutes; the protected pages keep their
 * existing auth gating via PartnerProtected. No new business logic.
 */
const BottomNav = () => {
    const { mpId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const gold = theme.palette.secondary.main;

    // Normalize the partner-prefixed pathname so the active checks below work
    // regardless of the mpId in the URL.
    const rest = location.pathname.replace(/^\/mp\/[^/]+/, '').replace(/\/+$/, '');
    const base = mpId ? `/mp/${mpId}` : '';

    const items = [
        {
            key: 'home',
            label: 'Home',
            Icon: HomeRoundedIcon,
            to: `${base}`,
            // Home covers the landing + the betting/race screens.
            active:
                rest === '' ||
                rest.includes('/racing') ||
                rest.includes('/marbleracing') ||
                rest.includes('/fixedracing')
        },
        {
            key: 'watch',
            label: 'Watch',
            Icon: VisibilityOutlinedIcon,
            to: `${base}/watch`,
            active: rest.includes('/watch')
        },
        {
            key: 'sales',
            label: 'Sales',
            Icon: TrendingUpRoundedIcon,
            to: `${base}/transactions`,
            active: rest.includes('/transactions')
        },
        {
            key: 'payouts',
            label: 'Payouts',
            Icon: PaidOutlinedIcon,
            to: `${base}/payout`,
            active: rest.includes('/payout') || rest.includes('/raceresults')
        },
        {
            key: 'wallet',
            label: 'Wallet',
            Icon: AccountBalanceWalletOutlinedIcon,
            to: `${base}/winnings`,
            active: rest.includes('/winnings')
        }
    ];

    return (
        <Paper
            elevation={0}
            square
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: (t) => t.zIndex.appBar,
                backgroundColor: '#0f0f0f',
                borderTop: '1px solid rgba(255,255,255,0.08)',
                // Respect iOS safe-area so the bar isn't under the home indicator.
                pb: 'env(safe-area-inset-bottom)'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    maxWidth: 600,
                    mx: 'auto',
                    px: 1
                }}
            >
                {items.map(({ key, label, Icon, to, active }) => (
                    <ButtonBase
                        key={key}
                        onClick={() => to && navigate(to)}
                        sx={{
                            flex: 1,
                            flexDirection: 'column',
                            gap: 0.5,
                            py: 1,
                            minHeight: 56,
                            color: active ? gold : 'rgba(255,255,255,0.6)',
                            transition: 'color .2s',
                            '&:hover': { color: active ? gold : 'rgba(255,255,255,0.85)' }
                        }}
                    >
                        <Icon sx={{ fontSize: 24 }} />
                        <Typography
                            sx={{
                                fontSize: '0.7rem',
                                fontWeight: active ? 700 : 500,
                                lineHeight: 1,
                                letterSpacing: '0.01em'
                            }}
                        >
                            {label}
                        </Typography>
                    </ButtonBase>
                ))}
            </Box>
        </Paper>
    );
};

export default BottomNav;
